import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import injectSheet from 'react-jss';

import { Button } from '~/eds/src/components/Button/Button';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { parseDate } from '~/eds/src/utils/parseDate';
import { DataFieldType } from '~/enums/DataFieldType';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';

import { black5 } from '../../../assets/shared-styles/general';
import { getOptions } from '../../Modals/AlertModal/AlertModal.options';
import EcDatepicker from '../EcDatepicker/EcDatepicker';
import EcInput from '../EcInput/EcInput';
import EcMultipleSelectValue from '../EcMultipleSelectValue/EcMultipleSelectValue';
import EcSelect from '../EcSelect/EcSelect';
import {
  mapOptionsToValues,
  mapValuesToOptions,
} from '../EcSelect/EcSelect.utils';
import FieldValuesMultiSelect from '../FieldValuesMultiSelect/FieldValuesMultiSelect';
import FieldValuesSingleSelect from '../FieldValuesSingleSelect/FieldValuesSingleSelect';
import SubArrowIcon from '../Icons/SubArrowIcon';
import styles from './EcFilterField.styles';
import {
  checkBetweenDatesRelationAndResetValues,
  coerceInputValueToNumber,
} from './EcFilterField.utils';
import { withFilterFieldOptions } from './withFilterFieldOptions';

class EcFilterField extends Component {
  constructor(props) {
    super(props);

    this.handleOnRelationChange = this.handleOnRelationChange.bind(this);
  }

  checkValuesAndResetError(value) {
    const { relation, type } = this.props;

    if (value && this.props.hasOwnProperty('error')) {
      if (type === DataFieldType.BOOLEAN || type === DataFieldType.ATTACHMENT) {
        return { ...value, error: false };
      }

      if (relation) {
        switch (type) {
          case DataFieldType.ARRAY_MULTIPLE:
            if (value.first_value && value.first_value.length) {
              return { ...value, error: false };
            }
            break;
          case DataFieldType.ARRAY_SINGLE:
          case DataFieldType.STRING:
          case DataFieldType.TEXT_AREA:
            if (value.first_value) {
              return { ...value, error: false };
            }
            break;
          case DataFieldType.DATE:
          case DataFieldType.NUMBER:
            if (
              ['next', 'last', 'between_dates', 'between'].includes(
                relation.value,
              )
            ) {
              const otherValue =
                Object.keys(value)[0] === 'first_value'
                  ? 'second_value'
                  : 'first_value';
              if (this.props[otherValue]) {
                return { ...value, error: false };
              }
            } else {
              return { ...value, error: false };
            }
            break;
          default:
            break;
        }
      }
    }

    return value;
  }

  handleOnValueChange(value) {
    const { type } = this.props;

    let newValue = { ...value };
    newValue = this.checkValuesAndResetError(newValue);

    if (
      (type === DataFieldType.BOOLEAN || type === DataFieldType.ATTACHMENT) &&
      value &&
      !this.isRelationBlank(value.value)
    ) {
      newValue['relation'] = null;
    }

    this.props.onChange(newValue);
  }

  isRelationBlank(value) {
    return ['is_blank', 'is_not_blank'].includes(value);
  }

  resetDateFieldCondition(previousRelation, relation) {
    return (
      (previousRelation.value !== 'next' &&
        previousRelation.value !== 'last' &&
        (relation.value === 'next' || relation.value === 'last')) ||
      ((previousRelation.value === 'next' ||
        previousRelation.value === 'last') &&
        relation.value !== 'next' &&
        relation.value !== 'last')
    );
  }

  // Reset `first_value` and `second_value` if `is_blank` or `is_not_blank` options are selected.
  checkBlankRelationAndResetValues(fieldProperties) {
    const { relation } = fieldProperties;
    if (relation && this.isRelationBlank(relation.value)) {
      return { ...fieldProperties, first_value: null, second_value: null };
    }

    return fieldProperties;
  }

  // Reset `first_value` and `second_value` due to conflicts in date field input types (datepicker vs. text input + select)
  checkAndResetDateValues(fieldProperties) {
    const { type, relation: previousRelation } = this.props;
    if (type === DataFieldType.DATE && previousRelation) {
      const resetCondition = this.resetDateFieldCondition(
        previousRelation,
        fieldProperties.relation,
      );
      if (resetCondition) {
        return { ...fieldProperties, first_value: null, second_value: null };
      }
    }

    return fieldProperties;
  }

  checkValidityAndResetError(fieldProperties) {
    const { first_value } = this.props;
    const { relation } = fieldProperties;

    if (
      relation &&
      (first_value || this.isRelationBlank(relation.value)) &&
      this.props.hasOwnProperty('error')
    ) {
      fieldProperties = { ...fieldProperties, error: false };
    }

    return fieldProperties;
  }

  handleOnRelationChange(relation) {
    let fieldProperties = { relation };
    fieldProperties = this.checkBlankRelationAndResetValues(fieldProperties);
    fieldProperties = this.checkAndResetDateValues(fieldProperties);
    fieldProperties = this.checkValidityAndResetError(fieldProperties);
    fieldProperties = checkBetweenDatesRelationAndResetValues(fieldProperties);

    this.props.onChange(fieldProperties);
  }

  renderNumberContainer() {
    const { classes, relation, first_value, second_value, error, intl } =
      this.props;

    return (
      <div className={classes.valuesContainer}>
        <div className={classes.fieldContainer}>
          <EcInput
            input={{
              value: coerceInputValueToNumber(first_value),
              onChange: (e) =>
                this.handleOnValueChange({
                  first_value: coerceInputValueToNumber(e.target.value),
                }),
            }}
            placeholder={intl.formatMessage({
              id: 'EcFilterField.enterAmount2',
              defaultMessage: 'enter amount',
              description: 'Placeholder for number input',
            })}
            type="number"
            min="0"
            max="1000000000"
            step="0.01"
            error={error && !first_value}
          />
        </div>
        {relation && relation.value === 'between' && (
          <Fragment>
            <span className={classes.horizontalAndSeparator}>
              {intl.formatMessage({
                id: 'EcFilterField.andSeparator1',
                defaultMessage: 'and',
                description: 'Separator word between two values',
              })}
            </span>
            <div className={classes.fieldContainer}>
              <EcInput
                input={{
                  value: coerceInputValueToNumber(second_value),
                  onChange: (e) =>
                    this.handleOnValueChange({
                      second_value: coerceInputValueToNumber(e.target.value),
                    }),
                }}
                placeholder={intl.formatMessage({
                  id: 'EcFilterField.enterAmount',
                  defaultMessage: 'enter amount',
                  description: 'Placeholder for number input',
                })}
                type="number"
                min="0"
                max="1000000000"
                step="0.01"
                error={error && !second_value}
              />
            </div>
          </Fragment>
        )}
      </div>
    );
  }

  renderStringContainer() {
    const { classes, first_value, error, intl } = this.props;

    return (
      <div className={classes.valuesContainer}>
        <div className={classes.fieldContainer}>
          <EcInput
            input={{
              value: first_value || '',
              onChange: (e) =>
                this.handleOnValueChange({ first_value: e.target.value }),
            }}
            placeholder={intl.formatMessage({
              id: 'EcFilterField.enterValue',
              defaultMessage: 'enter value',
              description: 'Placeholder for text input',
            })}
            error={error && (!first_value || !first_value.trim())}
          />
        </div>
      </div>
    );
  }

  renderDateContainer() {
    const {
      classes,
      flags,
      relation,
      first_value,
      second_value,
      error,
      name,
      intl,
      formatDate,
    } = this.props;
    const enableLocalization = flags[FlagType.LanguageSelect];
    const { DATE_PERIOD_OPTIONS } = getOptions(intl);

    if (relation && (relation.value === 'next' || relation.value === 'last')) {
      const defaultValue = second_value
        ? mapValuesToOptions([second_value])
        : null;

      return (
        <div className={classes.valuesContainer}>
          <EcInput
            input={{
              value: first_value || '',
              type: 'text',
              onChange: (e) =>
                this.handleOnValueChange({
                  first_value: Number(e.target.value),
                }),
            }}
            width="64px"
            error={error && !first_value}
          />
          <hr className={classes.blueLine} />
          <div className={classes.fieldContainer}>
            <EcSelect
              aria-label={name}
              value={defaultValue}
              options={DATE_PERIOD_OPTIONS}
              onChange={({ value }) =>
                this.handleOnValueChange({ second_value: value })
              }
              error={error && !second_value}
            />
          </div>
        </div>
      );
    }

    const firstFieldContainerClass =
      !first_value && error
        ? classes.fieldContainerError
        : classes.fieldContainer;
    const secondFieldContainerClass =
      !second_value && error
        ? classes.fieldContainerError
        : classes.fieldContainer;

    return (
      <div className={classes.valuesContainer}>
        <div className={firstFieldContainerClass}>
          {enableLocalization ? (
            <DateInput
              max={second_value ? parseDate(second_value) : null}
              value={first_value ? parseDate(first_value) : null}
              width="240px"
              onChange={(date) => {
                this.handleOnValueChange({
                  first_value: date ? formatDate(date, 'iso') : null,
                });
              }}
            />
          ) : (
            <EcDatepicker
              date={first_value ? parseDate(first_value) : null}
              onDateChange={(date) => {
                this.handleOnValueChange({
                  first_value: date ? formatDate(date, 'iso') : null,
                });
              }}
              maxDate={second_value ? parseDate(second_value) : null}
              placeholder={intl.formatMessage({
                id: 'EcFilterField.selectDate2',
                defaultMessage: 'Select date',
                description: 'Placeholder for date picker',
              })}
              isClearable={true}
            />
          )}
        </div>
        {relation && relation.value === 'between_dates' && (
          <Fragment>
            <span className={classes.horizontalAndSeparator}>
              {intl.formatMessage({
                id: 'EcFilterField.andSeparator2',
                defaultMessage: 'and',
                description: 'Separator word between two dates',
              })}
            </span>
            <div className={secondFieldContainerClass}>
              {enableLocalization ? (
                <DateInput
                  min={first_value ? parseDate(first_value) : null}
                  value={second_value ? parseDate(second_value) : null}
                  width="240px"
                  onChange={(date) => {
                    this.handleOnValueChange({
                      second_value: date ? formatDate(date, 'iso') : null,
                    });
                  }}
                />
              ) : (
                <EcDatepicker
                  date={second_value ? parseDate(second_value) : null}
                  onDateChange={(date) => {
                    this.handleOnValueChange({
                      second_value: date ? formatDate(date, 'iso') : null,
                    });
                  }}
                  minDate={first_value ? parseDate(first_value) : null}
                  placeholder={intl.formatMessage({
                    id: 'EcFilterField.selectDate',
                    defaultMessage: 'Select date',
                    description: 'Placeholder for date picker',
                  })}
                  isClearable={true}
                />
              )}
            </div>
          </Fragment>
        )}
      </div>
    );
  }

  renderArraySingleContainer() {
    const { classes, error, first_value } = this.props;

    const sharedProps = {
      value: first_value ? mapValuesToOptions([first_value]) : null,
      onChange: ({ value }) => this.handleOnValueChange({ first_value: value }),
      error: error && !first_value,
    };

    return (
      <div
        className={classes.valuesContainer}
        data-testid="ec-filter-field-values-single"
      >
        <div className={classes.fieldContainer}>
          <FieldValuesSingleSelect
            {...sharedProps}
            fieldId={this.props.id}
            fieldValues={this.props.values}
            searchSuggestions={true}
          />
        </div>
      </div>
    );
  }

  renderArrayMultipleContainer() {
    const { classes, first_value, error, id, name, values, intl } = this.props;

    const sharedProps = {
      value: first_value ? mapValuesToOptions(first_value) : [],
      onChange: (selectedOptions) =>
        this.handleOnValueChange({
          first_value: mapOptionsToValues(selectedOptions),
        }),
      placeholder: intl.formatMessage({
        id: 'EcFilterField.searchForValues',
        defaultMessage: 'Search for values...',
        description: 'Placeholder for multi-select search',
      }),
      noOptionsMessage: intl.formatMessage({
        id: 'EcFilterField.noValuesFound',
        defaultMessage: 'No values found',
        description: 'Message when no options are found',
      }),
      error: error && !(first_value && first_value.length),
      useMinInputLength: ['Counterparties', 'Internal Parties'].includes(name),
      allowSelectAll: true,
    };

    return (
      <div className={classes.valuesContainer}>
        <div
          className={classes.fieldContainer}
          data-testid="ec-filter-field-values-multiple"
        >
          <FieldValuesMultiSelect
            {...sharedProps}
            aria-label={name}
            fieldId={id}
            fieldValues={values}
            searchSuggestions={true}
          />
        </div>
      </div>
    );
  }

  renderMultipleValues() {
    const { classes, first_value, relation, intl } = this.props;

    if (relation && this.isRelationBlank(relation.value)) return;

    return (
      <div className={classes.multipleValuesContainer}>
        <SubArrowIcon color={black5} />
        <div className={classes.multipleValues}>
          {first_value && first_value.length ? (
            first_value.map((value, index) => (
              <EcMultipleSelectValue
                label={value}
                key={index}
                closeHandler={() => {
                  const multipleValues = [...first_value];
                  multipleValues.splice(index, 1);
                  this.handleOnValueChange({ first_value: multipleValues });
                }}
              />
            ))
          ) : (
            <div className={classes.multipleValuesEmpty}>
              {intl.formatMessage({
                id: 'EcFilterField.ensureSectionsHaveFilters',
                defaultMessage:
                  'Please make sure none of your sections are missing filters.',
                description: 'Warning message for empty filter sections',
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  getBooleanFieldDefaultValue() {
    const { first_value, relation, BOOLEAN_OPTIONS } = this.props;
    if (first_value) {
      return BOOLEAN_OPTIONS.find((option) => option.value === first_value);
    } else if (relation) {
      return BOOLEAN_OPTIONS.find((option) => option.value === relation.value);
    }

    return null;
  }

  getAttachmentDefaultValue() {
    const { first_value, relation, ATTACHMENT_OPTIONS } = this.props;
    if (first_value) {
      return ATTACHMENT_OPTIONS.find((option) => option.value === first_value);
    } else if (relation) {
      return ATTACHMENT_OPTIONS.find(
        (option) => option.value === relation.value,
      );
    }

    return null;
  }
  renderBooleanContainer() {
    const { classes, first_value, error, name, BOOLEAN_OPTIONS } = this.props;

    let defaultValue = this.getBooleanFieldDefaultValue();

    return (
      <div className={classes.valuesContainer}>
        <div className={classes.fieldContainer}>
          <EcSelect
            aria-label={name}
            value={defaultValue}
            options={BOOLEAN_OPTIONS}
            onChange={(newValue) => {
              this.isRelationBlank(newValue.value)
                ? this.handleOnRelationChange(newValue)
                : this.handleOnValueChange({ first_value: newValue.value });
            }}
            error={error && !first_value}
            isSearchable={false}
          />
        </div>
      </div>
    );
  }

  renderAttachmentContainer() {
    const { classes, first_value, error, name, ATTACHMENT_OPTIONS } =
      this.props;

    let defaultValue = this.getAttachmentDefaultValue();

    return (
      <div className={classes.valuesContainer}>
        <div className={classes.fieldContainer}>
          <EcSelect
            aria-label={name}
            value={defaultValue}
            options={ATTACHMENT_OPTIONS}
            onChange={(newValue) => {
              this.isRelationBlank(newValue.value)
                ? this.handleOnRelationChange(newValue)
                : this.handleOnValueChange({ first_value: newValue.value });
            }}
            error={error && !first_value}
            isSearchable={false}
          />
        </div>
      </div>
    );
  }

  renderValueContainer() {
    const { classes, type, relation } = this.props;

    if (
      type !== DataFieldType.BOOLEAN &&
      type !== DataFieldType.ATTACHMENT &&
      relation &&
      this.isRelationBlank(relation.value)
    )
      return;

    let valueContainer;
    switch (type) {
      case DataFieldType.NUMBER:
        valueContainer = this.renderNumberContainer();
        break;
      case DataFieldType.STRING:
      case DataFieldType.TEXT_AREA:
        valueContainer = this.renderStringContainer();
        break;
      case DataFieldType.DATE:
        valueContainer = this.renderDateContainer();
        break;
      case DataFieldType.ARRAY_SINGLE:
        valueContainer = this.renderArraySingleContainer();
        break;
      case DataFieldType.ARRAY_MULTIPLE:
        valueContainer = this.renderArrayMultipleContainer();
        break;
      case DataFieldType.BOOLEAN:
        valueContainer = this.renderBooleanContainer();
        break;
      case DataFieldType.ATTACHMENT:
        valueContainer = this.renderAttachmentContainer();
        break;
      default:
        valueContainer = [];
        break;
    }

    return (
      <Fragment>
        <hr className={classes.blueLine} />
        {valueContainer}
      </Fragment>
    );
  }

  renderOptionContainer() {
    const {
      classes,
      type,
      relation,
      error,
      name,
      DOCUMENT_ID_OPTIONS,
      getRelationOptions,
      intl,
    } = this.props;

    if (type === DataFieldType.BOOLEAN || type === DataFieldType.ATTACHMENT)
      return null;
    const options = getRelationOptions(type);

    return (
      <Fragment>
        <hr className={classes.blueLine} />
        <div
          className={classes.fieldContainer}
          data-testid="ec-filter-field-options"
        >
          <EcSelect
            aria-label={name}
            value={relation}
            options={name === 'Document Id' ? DOCUMENT_ID_OPTIONS : options}
            placeholder={intl.formatMessage({
              id: 'EcFilterField.selectOption',
              defaultMessage: 'select option',
              description: 'Placeholder for option selection',
            })}
            onChange={this.handleOnRelationChange}
            error={error && !relation}
          />
        </div>
      </Fragment>
    );
  }

  render() {
    const { classes, id, label, type, onRemoveClick, onFilterNameClick, intl } =
      this.props;

    const removeTooltip = intl.formatMessage(
      {
        id: 'EcFilterField.removeFieldTooltip',
        defaultMessage: 'Remove field: {label} ',
        description:
          'Tooltip for remove field button. Example: label = "Contract Title"',
      },
      { label },
    );

    return (
      <div className={classes.ecFilterFieldWrapper}>
        <div className={classes.ecFilterField}>
          <IconButton
            tooltip={removeTooltip}
            onClick={onRemoveClick}
            icon="x"
          />
          <Button
            id={`field-tooltip-${id}`}
            tooltip={label}
            text={label}
            icon="chevron-down"
            onClick={onFilterNameClick}
          />
          {this.renderOptionContainer()}
          {this.renderValueContainer()}
        </div>
        {type === DataFieldType.ARRAY_MULTIPLE && this.renderMultipleValues()}
      </div>
    );
  }
}

EcFilterField.propTypes = {
  id: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  entity: PropTypes.string,
  relation: PropTypes.object,
  first_value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  second_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRemoveClick: PropTypes.func.isRequired,
  onFilterNameClick: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

EcFilterField = injectSheet(styles)(
  withFlags(withFilterFieldOptions(injectIntl(withFormatDate(EcFilterField)))),
);

export default EcFilterField;
